import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useServiceCategory, { useServiceCategoryForm } from '@hooks/useServiceCategory'
import useServices from '@hooks/useServices'

import type { ServiceCategoryModel } from '@models/types'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditServiceCategoryModal'

type ModalCallbacks = {
  closeModal: () => void,
  createServiceCategory: (payload: HandleCallbackActionParams) => Promise<object>,
  updateServiceCategory: (payload: HandleCallbackActionParams) => Promise<object>,
}

type CreateOrEditServiceCategoryModalProps = {
  callbacks: ModalCallbacks,
  createFn: Function,
  entityState: Partial<ServiceCategoryModel>,
  updateFn: Function,
}

const createOrEditServiceCategory = (params: CreateOrEditServiceCategoryModalProps) => {
  const {
    callbacks, createFn, entityState, updateFn,
  } = params

  const { closeModal, createServiceCategory, updateServiceCategory } = callbacks || {}

  const [text, actionFn] = entityState.id ? ['Updated', updateFn] : ['Created', createFn]

  const serviceCategoryPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Service Category ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return updateServiceCategory(serviceCategoryPayload)

  // @ts-expect-error TS2345
  return createServiceCategory(serviceCategoryPayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateOrEditServiceCategoryModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { serviceCategory } = modalPayload

  const {
    callbacks: {
      createServiceCategory: createFn,
      updateServiceCategory: updateFn,
    },
    creating,
    updating,
  } = useServiceCategory(serviceCategory)

  const {
    entityState,
    entityState: {
      id,
      title,
      service_id,
    },
    errors,
    handlers,
    saveEnabled,
  } = useServiceCategoryForm(serviceCategory, { validateOn: 'change' })

  const { filteredServices } = useServices()

  const updateOrCreateButtonText = id ? 'Save Service Category' : 'Create Service Category'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Service Category" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Service" marginTop="large" errorMessage={errors.service_id}>
            {/*
             // @ts-expect-error TS2322 */}
            <select data-value-type="integer" name="service_id" value={service_id} {...handlers}>
              {!id && <option value="">Please Select</option>}
              {filteredServices.map(service => (
                <option key={service.id} value={service.id}>{service.title}</option>
              ))}
            </select>
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrEditServiceCategory({
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? updateOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditServiceCategoryModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
